<script>
import { validationMixin } from 'vuelidate'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import { formatter } from '@/helpers/formatter'
import { requiredIf, required, email } from 'vuelidate/lib/validators'
import { mapActions, mapGetters, mapState } from 'vuex'
import { Salutation } from '@/enums/Salutation'
import CustomAlert from '@/components/common/CustomAlert.vue'
import Avv from '@/components/common/Avv.vue'
export default {
  components: {
    Avv,
    CustomAlert
  },
  mixins: [validationMixin, formatter, fieldValidationsMixin],
  validations: {
    email: { required, email },
    salutation: { required },
    firstName: { required },
    lastName: { required },
    address: { required },
    zipCode: {
      required,
      onlyDigits(val) {
        const reg = /^\d+$/
        return reg.test(val)
      },
      correctLength(val) {
        return val != null && val.length === 5
      }
    },
    city: { required },
    chkB2bOnly: {
      checked(val) {
        return val
      }
    },
    taxId: {
      isGermanTaxId: requiredIf(function() {
        return (
          this.salutation === 2 &&
          this.taxId !== null &&
          /^(DE)+[0-9]{9}$/.test(this.taxId)
        )
      }),
      hasGermanCountryCode: requiredIf(function() {
        return (
          this.salutation === 2 &&
          this.taxId !== null &&
          /^(DE)/.test(this.taxId)
        )
      }),
      hasUniqueNumbers: requiredIf(function() {
        return (
          this.salutation === 2 &&
          this.taxId !== null &&
          /[0-9]{9}$/.test(this.taxId)
        )
      })
    }
  },
  props: {
    hideInfoText: { type: Boolean }, // hides the info text
    shrink: { type: Boolean } // shrinks the component
  },
  data: vm => ({
    devStage: window.config.VUE_APP_STAGE !== 'PROD',
    isAVVConfirmed: false,
    salutations: [
      {
        value: Salutation.Sir,
        text: vm.$t('form.label.mr')
      },
      {
        value: Salutation.Madam,
        text: vm.$t('form.label.mrs')
      },
      {
        value: Salutation.Company,
        text: vm.$t('form.label.company')
      }
    ],
    salutation: '',
    firstName: '',
    lastName: '',
    address: '',
    addressAddition: '',
    zipCode: '',
    city: '',
    country: vm.$t('form.label.germany'),
    phoneNumber: '',
    email: vm.$store.getters.username,
    customerNumber: '',
    onsCustomerNo: '',
    taxId: '',
    accepted: '',
    submitError: false,
    showNotification: false,
    chkB2bOnly: false,
    registerStatus: {
      type: 'error',
      msg: ''
    },
    loading: false
  }),
  computed: {
    ...mapState({
      userProfile: state => state.profile
    }),
    ...mapGetters(['hasAVV', 'username']),
    backgroundColor() {
      return this.shrink ? 'white' : 'transparent'
    }
  },
  created() {
    this.assignUserProfileValues()
  },
  methods: {
    ...mapActions(['setUserProfile']),
    assignUserProfileValues() {
      // this.userProfile is loaded from store
      this.salutation = this.userProfile.salutation
      this.firstName = this.userProfile.firstName
      this.lastName = this.userProfile.lastName
      this.address = this.userProfile.address
      this.addressAddition = this.userProfile.addressAddition
      this.zipCode = this.userProfile.zipCode
      this.city = this.userProfile.city
      this.phoneNumber = this.userProfile.phoneNumber
      this.customerNumber = this.userProfile.customerNumber
      this.onsCustomerNo = this.userProfile.onsCustomerNo
      this.taxId = this.userProfile.taxId
    },
    getFirstNameLabel() {
      if (this.salutation === Salutation.Company) {
        return this.$t('form.label.company.name')
      } else {
        return this.$t('form.label.first.name')
      }
    },
    getLastNameLabel() {
      if (this.salutation === Salutation.Company) {
        return this.$t('form.label.company.contact')
      } else {
        return this.$t('form.label.last.name')
      }
    },
    changeCountry() {
      this.$nextTick(() => {
        this.country = this.$t('form.label.germany')
      })
    },
    changeEmail() {
      this.$nextTick(() => {
        this.email = this.username
      })
    },
    acceptAVV(value) {
      this.isAVVConfirmed = value
    },
    validateFieldsExceptTaxId() {
      for (const key in this.$v.$params) {
        if (key !== 'taxId') {
          this.$v[key].$touch()
        }
      }
    },
    async submit() {
      // this.$v.$touch()
      this.validateFieldsExceptTaxId()
      this.showNotification = false
      this.submitError = false
      let companyName = ''
      let companyContactName = ''
      if (this.salutation === Salutation.Company) {
        companyName = this.firstName
        companyContactName = this.lastName
      }
      if (this.$v.$invalid) {
        this.submitError = true
      } else {
        this.loading = true
        return this.$userService
          .saveUserProfile({
            userName: this.username,
            salutation: this.salutation,
            firstName: this.firstName,
            lastName: this.lastName,
            address: this.address,
            addressAddition: this.addressAddition,
            zipCode: this.zipCode,
            city: this.city,
            country: this.country,
            phoneNumber: this.phoneNumber,
            companyName: companyName,
            companyContactName: companyContactName,
            taxId: this.taxId,
            alwaysAcceptAVV: true
          })
          .then(response => {
            this.showNotification = true
            this.setUserProfile(response) // set/save userProfile in store
            this.assignUserProfileValues()
            this.loading = false
            this.$emit('submitted', true)
          })
          .catch(() => {
            this.submitError = true
            this.loading = false
          })
          .finally(() => {
            setTimeout(() => {
              this.showNotification = false
            }, 3000)
          })
      }
    }
  }
}
</script>
<template name="base-data-form">
  <v-card
    flat
    tile
    color="transparent"
    :class="{ shrink: shrink }"
    class="base-data-form pa-4">
    <v-card-subtitle v-if="!hideInfoText">
      <v-icon>mdi-information-outline</v-icon>
      {{ $t('form.billing.data.info') }}
    </v-card-subtitle>
    <v-card-text>
      <v-row align="center">
        <v-col
          class="d-flex"
          :class="$vuetify.breakpoint.mdAndDown ? 'pb-1 pt-7' : ''"
          cols="12">
          <v-select
            id="salutation"
            v-model="salutation"
            :items="salutations"
            item-value="value"
            item-text="text"
            :label="$t('form.label.salutation')"
            :error-messages="fieldNeccessary('salutation', $v)"
            outlined
            dense
            required
            :background-color="backgroundColor">
          </v-select>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="firstName"
            v-model="firstName"
            :label="getFirstNameLabel()"
            :error-messages="fieldNeccessary('firstName', $v)"
            outlined
            dense
            required
            :background-color="backgroundColor"
            @input="$v.firstName.$touch()"/>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="lastName"
            v-model="lastName"
            :label="getLastNameLabel()"
            :error-messages="fieldNeccessary('lastName', $v)"
            outlined
            dense
            required
            :background-color="backgroundColor"
            @input="$v.lastName.$touch()"/>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="address"
            v-model="address"
            :label="$t('form.label.address')"
            :error-messages="fieldNeccessary('address', $v)"
            outlined
            dense
            required
            :background-color="backgroundColor"
            @input="$v.address.$touch()"/>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="addressAddition"
            v-model="addressAddition"
            :label="$t('form.label.address.extension')"
            outlined
            dense
            required
            :background-color="backgroundColor"/>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="zipCode"
            v-model="zipCode"
            :label="$t('form.label.zip.code')"
            :error-messages="[
              ...fieldNeccessary('zipCode', $v),
              ...zipCodeErrors('zipCode', $v)
            ]"
            outlined
            dense
            required
            :background-color="backgroundColor"
            @input="$v.zipCode.$touch()" />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="city"
            v-model="city"
            :label="$t('form.label.city')"
            :error-messages="fieldNeccessary('city', $v)"
            outlined
            dense
            required
            :background-color="backgroundColor"
            @input="$v.city.$touch()" />
        </v-col>
        <v-col cols="12" :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="country"
            v-model="country"
            :label="$t('form.label.country')"
            outlined
            dense
            filled
            @input="changeCountry()">
            <v-tooltip slot="prepend" top>
              <template #activator="{ on }">
                <v-icon color="primary" v-on="on"
                >mdi-alert-circle-outline
                </v-icon>
              </template>
              <span>{{ $t('form.label.tooltip.country') }}</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="email"
            v-model="email"
            :label="$t('form.label.email')"
            :error-messages="[
              ...fieldNeccessary('email', $v),
              ...emailInvalid('email', $v)
            ]"
            outlined
            dense
            required
            disabled
            filled
            @input="changeEmail()">
            <v-tooltip slot="prepend" top>
              <template #activator="{ on }">
                <v-icon color="primary" v-on="on">
                  mdi-alert-circle-outline
                </v-icon>
              </template>
              <span>{{ $t('form.no.email.change.possible') }}</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="phoneNumber"
            v-model="phoneNumber"
            :label="$t('form.label.phone')"
            outlined
            dense
            :background-color="backgroundColor" />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="customerNumber"
            v-model="customerNumber"
            :label="$t('form.label.customer.number')"
            outlined
            dense
            filled
            readonly>
          </v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="onsCustomerNo"
            v-model="onsCustomerNo"
            label="onsCustomerNo"
            outlined
            dense
            filled
            readonly>
          </v-text-field>
        </v-col>
        <v-col cols="12" :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="taxId"
            v-model="taxId"
            :label="$t('form.label.tax.id')"
            :error-messages="[...taxIdErrors('taxId', $v)]"
            outlined
            dense
            :background-color="backgroundColor" />
        </v-col>
      </v-row>
      <CustomAlert :show-alert="submitError" alert-type="error">
        <span class="body-2 cms_validatorMsg">
          {{ $t('form.billing.data.error') }}
        </span>
      </CustomAlert>
      <CustomAlert :show-alert="showNotification" alert-type="success">
        <span class="body-2">{{ $t('form.billing.data.success') }}</span>
      </CustomAlert>
      <v-checkbox
        id="b2bOnly"
        v-model="chkB2bOnly"
        :error-messages="checkbox($v.chkB2bOnly, $v)"
        :label="$t('form.trader')"
        required
        :background-color="backgroundColor"
        @change="$v.chkB2bOnly.$touch()"
        @blur="$v.chkB2bOnly.$touch()">
      </v-checkbox>
      <v-row v-if="hasAVV">
        <v-col v-html="$t('form.hint.avv', { date: formatLongDate(userProfile.acceptAvvDateTime) })"></v-col>
      </v-row>
      <v-row v-if="!hasAVV">
        <v-col>
          <Avv
            :hide-title="true"
            :hide-submit="true"
            :set-avv-checkbox="!!userProfile.alwaysAcceptAVV"
            @update:alwaysAcceptAVV="acceptAVV" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-btn
        id="saveBaseData"
        :loading="loading"
        :disabled="
          !chkB2bOnly || (!isAVVConfirmed && !userProfile.acceptAvvDateTime)"
        class="text-center px-8 cms_register"
        color="primary"
        depressed
        @click.stop="submit">
        {{
          $vuetify.breakpoint.smAndDown
            ? $t('button.save')
            : $t('button.save.profile')
        }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<style lang="scss" scoped>
.shrink.base-data-form {
  & > .v-card__text,
  & > .v-card__text {
    padding: 0px !important;

    & > .container {
      padding: 0px !important;
    }
  }
  div[class^='col'] {
    padding: 2px 12px 5px 12px !important;
  }
}
</style>
